<!-- StagedRelease.svelte — the builds that are uploaded but offered to NOBODY.
     A desktop release uploads installers only and withholds the update manifests, so a
     fresh build sits here until it is promoted — which must not happen before the
     extension version live in the Chrome Web Store has caught up. -->
<script lang="ts">
  import { Rocket, Clock } from "lucide";
  import { Icon, Button } from "@price-check/ui";
  import type { ReleaseFeed, ReleaseOs } from "../../lib/api";
  import Card from "../../lib/ui/Card.svelte";
  import Pill from "../../lib/ui/Pill.svelte";

  let {
    feed,
    busy,
    label,
    onpromote,
  }: {
    feed: ReleaseFeed;
    busy: string | null;
    label: (os: ReleaseOs) => string;
    onpromote: (version: string) => void;
  } = $props();

  const staged = $derived(feed.versions.filter((v) => v.state === "held"));
</script>

<Card
  title="Staged — not offered yet"
  subtitle="A release uploads installers only; the update feed is left untouched. Nothing reaches a customer until it is promoted here."
>
  {#snippet actions()}
    <Pill tone={staged.length ? "amber" : "slate"}>
      {staged.length ? `${staged.length} withheld` : "none"}
    </Pill>
  {/snippet}

  <div class="text-sm text-muted">
    Chrome Web Store extension last recorded live:
    <span class="font-semibold text-ink">
      {feed.storeExtVersion ? `v${feed.storeExtVersion}` : "unknown"}
    </span>
    — the gate compares a staged desktop version against it.
  </div>

  {#if staged.length === 0}
    <p class="mt-3 text-sm text-muted">Nothing is waiting. Every uploaded build is published.</p>
  {:else}
    <div class="mt-3 flex flex-col divide-y divide-border">
      {#each staged as v (v.version)}
        <div class="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-ink">{v.version}</span>
              <Pill tone="amber">withheld</Pill>
            </div>
            <div class="text-xs text-muted">
              {v.platforms.length ? v.platforms.map(label).join(" · ") : "no installers"}
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={busy !== null || v.platforms.length === 0}
            onclick={() => onpromote(v.version)}
          >
            <Icon icon={Rocket} size={14} />
            {busy === v.version ? "Promoting…" : `Promote ${v.version}`}
          </Button>
        </div>
      {/each}
    </div>

    <div class="mt-4 flex items-start gap-2 rounded-md border border-border bg-surface-2 px-3.5 py-3 text-xs text-muted">
      <Icon icon={Clock} size={14} class="mt-0.5 shrink-0" />
      <span>
        Prefer the gated command — it reads the store's live version itself, promotes only
        when the store is level, and is safe to re-run while review is pending:
        <code class="font-semibold text-ink">pnpm release:promote</code>
        (<code>--dry-run</code> to preview). Promoting here is the manual override.
      </span>
    </div>
  {/if}
</Card>
